import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { getMapYears, getMetricsFor, getProvincesMetrics, MapAutonomousCommunity, MapMunicipality, MapProvince, MapProvinceMetric, MapSectionMetric, MapYear } from '../services/map-municipalities.service'
import { MapMetric } from '../services/map-metrics.service'
import { Map } from './Map'
import { ErrorState } from './ErrorState'
import { useGetProvincesTopojson } from '../services/map-topojson.service'
import { GeoJSON } from 'highcharts'
import { getGeojson } from '../services/map-geojson.service';
import { getProvinceSectionsGeojson } from '../services/map-sections.service';
import { AVISO_VISTA_PROVINCIAL, AvisoZona, avisoDeZona, esZonaDemasiadoExtensa, estadoDeError } from '../services/map-error.util';
import { ADD_ALL_ID } from '../layouts/components/map-municipality-search/MapMunicipalitySearchForm';
import { filterAndRoundByYear } from './map-helper.util';
import { BoundingBox, decideLevel, provincesInView, shouldPreload } from './map-detail-level.util';
import { provinceBoxes as buildProvinceBoxes } from './map-bounds.util';

type Props = {
  autonomousCommunities: MapAutonomousCommunity[],
  provinces: MapProvince[],
  municipalities: MapMunicipality[],
  metric: MapMetric,
}

/** How the geometry and the values are joined when the map draws census sections. */
const sectionSeriesOptions = {
  joinBy: ['id', 'section_id'],
  tooltip: {
    footerFormat: '<span style="font-size:10px">Distrito {point.point.properties.district}, Sección {point.point.properties.section}</span>'
  },
}

/**
 * Shown when none of the sections the zoom asked for could be loaded (for instance if the
 * API does not serve them yet). The provincial map stays on screen: a broken map is worse
 * than a coarse one.
 */
const AVISO_SECCIONES = 'No se han podido cargar las secciones de la zona: sigues viendo el mapa por provincias.'

export const MapHelper: React.FC<Props> = ({ autonomousCommunities, provinces, municipalities, metric }) => {
  // We are going to use topojson for the whole spain map, since it is smaller
  const { loading: loadingProvincesTopojson, data: provincesTopojson } = useGetProvincesTopojson();

  const [sectionGeojson, setSectionGeojson] = useState<GeoJSON>();
  const [metricsData, setMetricsData] = useState<MapSectionMetric[]>([])
  const [loadingSection, setLoadingSection] = useState(false);

  const [provincesMetricsData, setProvincesMetricsData] = useState<MapProvinceMetric[]>([])
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  // Level of detail decided by the zoom. It only applies while no specific area is selected:
  // the map opens on provinces and, once few enough of them are on screen, it draws their
  // census sections; zooming back out returns to provinces. Every province in sight is drawn,
  // so the whole frame ends up with data.
  const [zoomSections, setZoomSections] = useState<{ provinceIds: number[]; geojson: GeoJSON }>();
  const [zoomMetricsData, setZoomMetricsData] = useState<MapSectionMetric[]>([]);
  const [avisoSecciones, setAvisoSecciones] = useState(false);
  // The camera follows the data only when the change was not caused by the user zooming:
  // otherwise the map would jump under their hands.
  const [fitToData, setFitToData] = useState(true);
  // Records instead of Map: in this file `Map` is the map component imported above.
  const loadedSections = useRef<Record<number, GeoJSON>>({});
  const sectionsInFlight = useRef<Record<number, Promise<GeoJSON>>>({});
  const zoomAbortRef = useRef<AbortController | null>(null);
  // Identifies the latest zoom request, so a slow answer cannot draw over a newer one.
  const zoomRequestId = useRef(0);

  // If the area cannot be loaded we say so on screen instead of leaving the map empty in silence
  const [errorZona, setErrorZona] = useState<AvisoZona>();
  // If the area is too large for section detail we fall back to the provincial view
  const [vistaProvincialPorTamano, setVistaProvincialPorTamano] = useState(false);

  const [years, setYears] = useState<MapYear[]>([]);
  const [selectedYear, setSelectedYear] = useState<MapYear>();

  const abortControllerRef = useRef<AbortController | null>(null);

  // Nothing selected (or "Spain" itself): the provinces are drawn and the zoom decides the level.
  const provincialView = autonomousCommunities.some(it => it.id === ADD_ALL_ID)
    || !(autonomousCommunities.length + provinces.length + municipalities.length);

  const provinceBoxes = useMemo(() => buildProvinceBoxes(provincesTopojson), [provincesTopojson]);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    getMapYears(controller.signal).then(data => {
      if (data) {
        setYears(data);
        setSelectedYear(data[0]);
      }
    }).catch(err => {
      if (err.name !== 'AbortError') {
        console.error('Error fetching map years:', err);
      }
    });

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (provincialView) {
      setSectionGeojson(undefined);
      setErrorZona(undefined);
      setVistaProvincialPorTamano(false);
      setProvincesMetricsData([]);
      // Back to the provincial view: the camera fits the country again.
      setFitToData(true);
      setLoadingMetrics(true);
      getProvincesMetrics(metric.id, controller.signal)
        .then(data => {
          if (data) {
            setProvincesMetricsData(data);
          }
        })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.error('[MapHelper] Error fetching province metrics:', err);
          }
        })
        .finally(() => setLoadingMetrics(false));

      return () => controller.abort();
    }
    // A specific area is selected: it decides both the geometry and the values, and the zoom
    // does not interfere. Whatever the zoom had opened is dropped.
    setZoomSections(undefined);
    setZoomMetricsData([]);
    setAvisoSecciones(false);
    setLoadingSection(true);
    setErrorZona(undefined);
    setVistaProvincialPorTamano(false);
    setSectionGeojson(undefined);
    setMetricsData([]);
    Promise.all([
      getGeojson({ autonomousCommunities, provinces, municipalities, signal: controller.signal }).then(data => {
        setSectionGeojson(data);
      }),
      getMetricsFor(metric.id, { autonomousCommunities, provinces, municipalities }, controller.signal).then(data => {
        setMetricsData(data);
      })
    ]).catch(err => {
      if (err.name === 'AbortError') {
        return;
      }
      const status = estadoDeError(err);
      if (esZonaDemasiadoExtensa(status)) {
        // The API cannot serve section detail for an area that large: instead of leaving the
        // area empty we draw the map by provinces and explain why.
        setVistaProvincialPorTamano(true);
        setErrorZona(undefined);
        setLoadingMetrics(true);
        getProvincesMetrics(metric.id, controller.signal)
          .then(data => {
            if (data) {
              setProvincesMetricsData(data);
            }
          })
          .catch(errProvincias => {
            if (errProvincias.name !== 'AbortError') {
              console.error('[MapHelper] Error fetching province metrics:', errProvincias);
              setVistaProvincialPorTamano(false);
              setErrorZona(avisoDeZona(estadoDeError(errProvincias)));
            }
          })
          .finally(() => setLoadingMetrics(false));
        return;
      }
      console.error('[MapHelper] Error fetching section data:', err);
      setErrorZona(avisoDeZona(status));
    }).finally(() => setLoadingSection(false));

    return () => {
      controller.abort();
    };
  },
    [metric, autonomousCommunities, provinces, municipalities, provincialView])

  // Values of the provinces the zoom has opened: same endpoint as the selected-area view,
  // but filtered to the provinces on screen, so section by section.
  useEffect(() => {
    if (!zoomSections) {
      return;
    }

    const controller = new AbortController();
    zoomAbortRef.current = controller;
    setLoadingSection(true);

    getMetricsFor(
      metric.id,
      {
        autonomousCommunities: [],
        provinces: zoomSections.provinceIds.map(id => ({ id, name: '' })) as MapProvince[],
        municipalities: [],
      },
      controller.signal,
    )
      .then(data => {
        if (data) {
          setZoomMetricsData(data);
        }
      })
      .catch(err => {
        if (err.name === 'AbortError') {
          return;
        }
        console.error('[MapHelper] Error fetching zoom section metrics:', err);
        setAvisoSecciones(true);
        setFitToData(false);
        setZoomSections(undefined);
      })
      .finally(() => setLoadingSection(false));

    return () => controller.abort();
  }, [metric, zoomSections])

  /**
   * Sections of a province, asked once: the preload and the switch that follows it share the
   * same request instead of racing each other.
   */
  const fetchSectionsOnce = useCallback((provinceId: number, signal?: AbortSignal): Promise<GeoJSON> => {
    const loaded = loadedSections.current[provinceId];
    if (loaded) {
      return Promise.resolve(loaded);
    }

    const inFlight = sectionsInFlight.current[provinceId];
    if (inFlight) {
      return inFlight;
    }

    const request = getProvinceSectionsGeojson(provinceId, signal)
      .then(data => {
        loadedSections.current[provinceId] = data;
        delete sectionsInFlight.current[provinceId];

        return data;
      })
      .catch(err => {
        delete sectionsInFlight.current[provinceId];
        throw err;
      });

    sectionsInFlight.current[provinceId] = request;

    return request;
  }, []);

  /**
   * Opens the section level for every province in sight at once: drawing a single one would
   * leave the rest of the frame empty, which is what the level is meant to cover. Provinces
   * that fail are simply left out as long as something else could be drawn.
   */
  const openZoomSections = useCallback((provinceIds: number[]) => {
    const controller = new AbortController();
    zoomAbortRef.current = controller;
    const requestId = ++zoomRequestId.current;

    Promise.allSettled(provinceIds.map(id => fetchSectionsOnce(id, controller.signal)))
      .then(results => {
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.error('[MapHelper] Error fetching sections of province', provinceIds[index], result.reason);
          }
        });

        // A newer zoom has already asked for something else: this answer is stale.
        if (requestId !== zoomRequestId.current) {
          return;
        }

        const loaded = results
          .filter((result): result is PromiseFulfilledResult<GeoJSON> => result.status === 'fulfilled')
          .map(result => result.value)
          .filter(Boolean);

        if (!loaded.length) {
          // The provincial map stays: it is already loaded, so the user keeps a usable map.
          setAvisoSecciones(true);
          return;
        }

        setAvisoSecciones(false);
        // The user is already looking at this area, so the camera must not move: the flag is
        // set before the geometry, because the two updates are not batched and the map would
        // fit itself to the new sections on that single frame in between.
        setFitToData(false);
        setZoomSections({
          provinceIds,
          geojson: {
            type: 'FeatureCollection',
            features: loaded.flatMap(geojson => (geojson as any)?.features ?? []),
          } as GeoJSON,
        });
      });
  }, [fetchSectionsOnce]);

  const preloadSections = useCallback((provinceIds: number[]) => {
    provinceIds.forEach(id => {
      fetchSectionsOnce(id).catch(() => {
        // A failed preload is not worth interrupting the user for: the switch itself will ask
        // again and explain any real problem.
      });
    });
  }, [fetchSectionsOnce]);

  /** The map says what it is showing; the rule says which level that view deserves. */
  const handleViewChange = useCallback((view: BoundingBox) => {
    if (!provincialView || !provinceBoxes.length) {
      return;
    }

    const targets = provincesInView(view, provinceBoxes).map(province => province.id);
    const level = decideLevel(view, provinceBoxes, zoomSections ? 'sections' : 'provinces');

    if (level === 'sections' && targets.length) {
      // Only when the set of provinces changes: panning inside the same group must not refetch.
      if (!zoomSections || zoomSections.provinceIds.join(',') !== targets.join(',')) {
        openZoomSections(targets);
      }
      return;
    }

    if (zoomSections) {
      // Back to provinces: the provincial values are already loaded, so this is instant. The
      // camera flag goes first, for the same reason as when entering the section level.
      setFitToData(false);
      setZoomSections(undefined);
      setZoomMetricsData([]);
      setAvisoSecciones(false);
      return;
    }

    if (targets.length && shouldPreload(view, provinceBoxes)) {
      preloadSections(targets);
    }
  }, [provincialView, provinceBoxes, zoomSections, openZoomSections, preloadSections]);

  const loading = loadingProvincesTopojson || loadingMetrics || loadingSection || !selectedYear;

  const mapContent = zoomSections
    ? (
      <Map
        autonomousCommunities={autonomousCommunities}
        provinces={provinces}
        municipalities={municipalities}
        metric={metric}
        year={selectedYear}
        geographicalData={zoomSections.geojson}
        metricData={selectedYear
          ? filterAndRoundByYear(zoomMetricsData, selectedYear.name)
          : []
        }
        seriesOptions={sectionSeriesOptions}
        fitToData={fitToData}
        onViewChange={handleViewChange}
        years={years}
        handleYearSelection={setSelectedYear}
        loading={loading}
      ></Map>
    )
    : sectionGeojson
    ? (
      <Map
        autonomousCommunities={autonomousCommunities}
        provinces={provinces}
        municipalities={municipalities}
        metric={metric}
        year={selectedYear}
        geographicalData={sectionGeojson}
        metricData={selectedYear 
          ? filterAndRoundByYear(metricsData, selectedYear.name)
          : []
        }
        seriesOptions={sectionSeriesOptions}
        years={years}
        handleYearSelection={setSelectedYear}
        loading={loading}
      ></Map>
    )
    : (
      <Map
        autonomousCommunities={autonomousCommunities}
        provinces={provinces}
        municipalities={municipalities}
        metric={metric}
        year={selectedYear}
        geographicalData={provincesTopojson}
        metricData={selectedYear 
          ? filterAndRoundByYear(provincesMetricsData, selectedYear.name)
          : []
        }
        seriesOptions={{ joinBy: ['CPRO', 'province_id'] }}
        fitToData={fitToData}
        onViewChange={handleViewChange}
        years={years}
        handleYearSelection={setSelectedYear}
        loading={loading}
      ></Map>
    );

  return (
    <>
      {/* The notices go above the map, outside the map box: the loader veil is painted by the
          map itself, inside its own box, so the spinner is centred on the map and not on the
          whole card (year selector above, metadata below). */}
      {!errorZona && vistaProvincialPorTamano && (
        <AvisoMapa role="status">{AVISO_VISTA_PROVINCIAL}</AvisoMapa>
      )}
      {!errorZona && avisoSecciones && (
        <AvisoMapa role="status">{AVISO_SECCIONES}</AvisoMapa>
      )}
      {errorZona ? (
        <ErrorState title={errorZona.title} message={errorZona.message} fullPage={false} />
      ) : (
        mapContent
      )}
    </>
  )
}

/** Notice inside the section (it does not cover the map): brand tint with ink text. */
const AvisoMapa = styled.p`
  margin: 0;
  padding: 12px 16px;
  background-color: rgba(47, 205, 222, 0.14);
  color: #222;
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
`
